import React, { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, Typography } from "antd";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { getQuoteApiError, sendQuote } from "functions/api/quotes";
import { QUOTE_SEND_STATE, getQuoteSendState } from "functions/quotes";

const { Text } = Typography;

/**
 * "Offerte versturen". Een dialoog en geen losse knop, om dezelfde reden als bij de
 * Exact-orderbevestiging: er gaat post naar een klant. Je ziet welke versie er uitgaat, of die al
 * eerder verstuurd is, en naar welk adres — dat laatste is aan te passen.
 */
const SendQuoteModal = ({ quote, open, onClose, onSent }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !quote) return;
    setError(null);
    form.setFieldsValue({ email: quote.customer?.email || "" });
  }, [open, quote, form]);

  if (!quote) return null;

  const sendState = getQuoteSendState(quote);
  const version = quote.version || 1;

  const handleSend = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      const result = await sendQuote(quote.id, values.email);
      onSent?.(result);
      onClose();
    } catch (err) {
      console.error("Offerte versturen mislukt:", err);
      setError(getQuoteApiError(err, t("quote.send.failed")));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("quote.send.title")}
      okText={t("quote.send.ok")}
      cancelText={t("cancel")}
      confirmLoading={isSending}
      onOk={handleSend}
      onCancel={onClose}
      destroyOnClose
    >
      <p style={{ marginBottom: 16 }}>
        {t("quote.send.intro", {
          number: quote.quoteNumber,
          version,
          customer: quote.customer?.company || `${quote.customer?.firstName || ""} ${quote.customer?.lastName || ""}`,
        })}
      </p>

      {sendState.state === QUOTE_SEND_STATE.SENT ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("quote.send.already_sent", {
            date: dayjs.unix(sendState.at).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
            email: sendState.to || "-",
          })}
        />
      ) : null}
      {sendState.state === QUOTE_SEND_STATE.CHANGED ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("quote.send.changed_since", {
            version: sendState.version,
            date: dayjs.unix(sendState.at).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
          })}
        />
      ) : null}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="email"
          label={t("quote.send.recipient")}
          rules={[
            { required: true, message: t("exact.confirmation.email_required") },
            { type: "email", message: t("exact.confirmation.email_invalid") },
          ]}
        >
          <Input placeholder="naam@bedrijf.nl" autoComplete="off" />
        </Form.Item>
      </Form>

      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("quote.send.explainer", {
          validUntil: quote.validUntil ? dayjs.unix(quote.validUntil).format(DATE_FORMAT_DD_MM_YYYY) : "-",
        })}
      </Text>

      {error ? <Alert type="error" showIcon style={{ marginTop: 16 }} message={error} /> : null}
    </Modal>
  );
};

export default SendQuoteModal;
